import React, {ReactElement} from 'react';
import {Col, Container, Row} from "react-bootstrap";
import TemplatePageLeftContent from "./template_page_left_content/template_page_left_content";
import {Page} from "../../model/page";
import {LAST_COMPONENT_ID} from "../../config/api_config";
import {ComponentId} from "../../model/component_id";
import "./template_page.scss";
import TemplateComponent, {TemplateComponentProps} from "./template_component/template_component";
import {COMPONENTS} from "../../config/components";
import {Helmet} from "react-helmet";
import TemplatePageFooter from "./template_page_footer/template_page_footer";
import TemplatePagePlaceholder from "./template_page_placeholder/template_page_placeholder";
import ErrorBoundary from "../../common_components/error_boundary/error_boundary";
import NotFoundComponent from "./not_found/not_found";
import {extraSmallLayout} from "../../utils/device";
import {HIGH_LEVEL_CONFIG} from "../../config/high_level_config";
import {pageLoaded} from "../../utils/utils";
import {HOME_PAGE_FOOTER_LINKS, SUBPAGE_FOOTER_ADDITIONAL_LINKS} from "../../config/footer_contents";
import {BreadcrumbPiece} from "../../utils/menu";
import TemplateCoursePagePlaceholder from "./template_course_page/template_course_page";

export interface TemplatePageProps<P = Page> extends TemplateComponentProps<P> {
    breadcrumbCallback: (breadcrumb: BreadcrumbPiece[]) => void;
    menuColorCallback: (color: string) => void;
    pageLoaded: () => void;
    logoAnimationCompleted: boolean;
    leftContentClass: any;
    path: string;

    navigate: (path: string) => void;
    changeLanguage: (language: string) => void;
}


/**
 * This page loads dynamically the list of components it will render;
 * the list is fetched using the current path, therefore this page can be used to render every page that uses the two column layout
 *
 * The page's data is stored in the state variable data, and after it has been retrieved it is also provided as a parameter to the callback method onDataLoaded
 */
class TemplatePage<P extends Page = Page, L extends TemplatePageLeftContent = TemplatePageLeftContent> extends TemplateComponent<P, TemplatePageProps<P>> {

    /**
     *  Every page state has at least the following variables:
     *  - data: the data the page will render
     *  - isFetching: true while we are fetching the page's data
     *  - logoAnimationCompleted: true if the logo animation has completed
     *  - placeholder: true while we are showing the placeholder content (before the first scroll)
     */
    state: {
        data?: Page,
        isFetching: boolean,
        placeholder: boolean
    } | any;

    baseColor: string = "dark";

    microformatsPageType = "h-entry";
    ogPageType = "website";

    constructor(props: TemplatePageProps<P>) {
        super(props);

        this.state = {
            isFetching: false,
            placeholder: true,
            notFound: false
        };
    }

    componentDidMount(): void {
        super.componentDidMount();

        // When the user scroll down we hide the placeholder content
        document.addEventListener('aos:in:components-list', (_) => {
            if (this.state.placeholder) {
                this.setState({placeholder: false});
            }
        });
    }

    /**
     * This method is called with the page's data after it has been fetched
     *
     * @param data - the data to display in the page
     */
    onDataLoaded(data: Page) {
        console.log("NETWORK: Page data has been loaded", data);

        if (data) {

            if(data.title.includes("%s")){
                data.title = data.title.replace("%s", "");
            }

            // we update the navigation bar color based on the page
            if (extraSmallLayout() && data.mobile_navbar_color) {
                this.baseColor = data.mobile_navbar_color;
            } else if (data.desktop_navbar_color) {
                this.baseColor = data.desktop_navbar_color;
            }

            // the placeholder contents are shown only if we define 'image' or 'video'
            if (!data.image && !data.video) {
                if (this.state.placeholder) {
                    this.setState({placeholder: false});
                }
            }

            if (data.internal_menu && data.internal_menu.length) {
                data.internal_menu = data.internal_menu.map(label => label !== "(skip)" ? label : "");
            }

            let path = "";
            try {
                if (data.path) {

                    path = data.path.hasOwnProperty("alias") ? (data.path as { alias: string }).alias : (data.path as string);

                    if (this.props.path && path && path !== this.props.path && !path.startsWith("{")) {
                        window.history.pushState({}, data.title, path);
                    }
                }

            } catch (e) {
                console.log("[ERROR]: An exception has been thrown trying to change the path to " + path, e);
            }

            this.setState({
                notFound: false
            });

            this.props.pageLoaded();
            this.props.menuColorCallback(this.baseColor);
            this.props.breadcrumbCallback([]);
        }

        pageLoaded();
    }

    onDataLoadingFailed() {
        console.log(`The page at ${window.location.pathname} has not been found`);

        this.baseColor = "dark";
        this.setState({
            notFound: true
        });
        this.props.menuColorCallback(this.baseColor);
        this.props.breadcrumbCallback([]);

        pageLoaded();
    }

    sectionId(section: string): string {
        return section.replace(/ /g, "-");
    }

    beforeRightComponentsList(page: P): ReactElement | React.ReactNodeArray | React.ReactPortal | undefined {
        return undefined;
    }

    afterRightComponentsList(page: P): ReactElement | React.ReactNodeArray | React.ReactPortal | undefined {
        return undefined;
    }

    /**
     * We override the render method to return the page structure, which is:
     * - components for the logo animation when loading the page
     * - page being shown
     *   o left column (navigation items, ...)
     *   o right column, with placeholder contents and the list of components to render
     * - footer
     */
    render() {

        let data: P = this.state.data;

        let aosAnimation = {};
        let rightColumn = {span: 12, offset: 0};

        if (data) {

            if (this.state.placeholder) {
                aosAnimation = {
                    "data-aos": "fade-up",
                    "data-aos-offset": 2,
                    "data-aos-id": "components-list",
                    "data-aos-anchor": "#root",
                    "data-aos-anchor-placement": "top-top",
                    "data-aos-duration": 1000
                }
            }

            // if we are in a two columns layout on desktop, the right column will be large 7/12 of the page
            if (data.two_columns && !extraSmallLayout()) {
                rightColumn = {span: 7, offset: 0 /*, offset: 5*/};
            }
        }

        if (this.state.notFound || ((!data || !data.desktop_navbar_color || (data.langcode && data.langcode !== "und" && data.langcode !== this.props.language)) && !this.state.isFetching)) {
            // if the fetching has completed but there is no data we return the 404 page

            // if the page has not been translated (and the url is the one with the page id),
            // the page in the other language is returned and its language property mismatch
            // otherwise no page is returned and notFound is true
            return (
                <NotFoundComponent type={this.state.notFound ? "PAGE" : "TRANSLATION"}
                                   language={this.props.language} changeLanguage={this.props.changeLanguage}/>
            );
        }

        let LeftContent = this.props.leftContentClass;
        return (
            <div id="page"
                 className={`${this.props.logoAnimationCompleted ? "" : "logo-animation"} ${extraSmallLayout() ? "mobile" : "desktop"}`}>
                <Container fluid={true} id="page-container"
                           className={`${this.microformatsPageType} ${this.state.placeholder ? "show-placeholder" : "show-contents"}`}>
                    {
                        // after we have loaded the page data, we can render the contents
                        (data) &&
                        <>
                            <Helmet defaultTitle="Luiss Guido Carli">
                                <title>{data.title + " | Luiss"}</title>
                                <meta property="og:title" content={data.title + " | Luiss"}/>
                                <meta name="twitter:title" property="twitter:title" content={data.title}/>

                                {data.subtitle &&
                                <meta name="description" property="description" content={data.subtitle}/>}
                                {data.subtitle && <meta property="og:description" content={data.subtitle}/>}
                                {data.subtitle && <meta name="twitter:description" property="twitter:description"
                                                        content={data.subtitle}/>}

                                <meta property="og:image"
                                      content={data.image ? data.image : HIGH_LEVEL_CONFIG.DEFAULT_IMAGE_URL}/>
                                <meta name="twitter:image" property="twitter:image"
                                      content={data.image ? data.image : HIGH_LEVEL_CONFIG.DEFAULT_IMAGE_URL}/>
                                <meta name="twitter:image:alt" property="twitter:image:alt" content={data.title}/>

                                {data.video && <meta name="og:video" property="og:video" content={data.video}/>}

                                <meta property="og:type" content={this.ogPageType}/>

                                <meta property="og:url" content={"https://www.luiss.it" + this.props.path}/>
                            </Helmet>

                            {data.two_columns && this.props.url?.includes("/course/") && this.props.path!=="/" &&
                                <Row>
                                    <TemplateCoursePagePlaceholder className={extraSmallLayout() ? "marginFixer buttonFix" : "buttonFix"} image={data.image} video={data.video} />
                                </Row>
                            }
                            <Row className="contents-row e-content">
                                {
                                    // two columns layout - desktop - the fixed left column
                                    !extraSmallLayout() && data.two_columns && this.props.path!=="/" &&
                                    <Col sm={5} xs={0} className="left-column fixed-left-column">
                                        <LeftContent page={data} language={this.props.language}
                                                     layoutColumns={(!extraSmallLayout() && data.two_columns) ? 2 : 1}/>
                                    </Col>
                                }
                                <Col sm={rightColumn}>
                                    {
                                        // two columns layout - desktop - the image placeholder shown at the beginning
                                        !extraSmallLayout() && data.two_columns && this.props.path!=="/" && !this.props.url?.includes("/course/") &&
                                        <TemplatePagePlaceholder className={"disappearing"}
                                                                 image={data.image} video={data.video}/>
                                    }
                                    {
                                        // one column layout - desktop - the left contents with the placeholder on the right
                                        !extraSmallLayout() && !data.two_columns && this.props.path!=="/" && !this.props.url?.includes("/summer_course/") &&
                                        <Container fluid={true} className="one-column-title-container">
                                            <Row>
                                                <Col sm={5} xs={0} className="left-column">
                                                    <LeftContent page={data} language={this.props.language}
                                                        layoutColumns={(!extraSmallLayout() && data.two_columns) ? 2 : 1} />
                                                </Col>
                                                <Col xs={12} sm={7}>
                                                    <TemplatePagePlaceholder image={data.image} video={data.video}/>
                                                </Col>
                                            </Row>
                                        </Container>
                                    }
                                    {
                                        // mobile - we show the mobile version of the left content (always in one column)
                                        extraSmallLayout() && !this.props.url?.includes("/summer_course/") && this.props.path!=="/" &&
                                        <LeftContent page={data} language={this.props.language}
                                                     layoutColumns={(!extraSmallLayout() && data.two_columns) ? 2 : 1}/>
                                    }
                                    <div id="components-list" {...aosAnimation}>
                                        {
                                            this.beforeRightComponentsList(data)
                                        }
                                        {
                                            data.components.map((c: ComponentId, index: number) => {

                                                try {
                                                    let CTemplateComponent = COMPONENTS.get(c.type);
                                                    if (!CTemplateComponent) return null;

                                                    let idProps = {};
                                                    let dataProps = {};

                                                    if (data.internal_menu && data.internal_menu.length) {
                                                        if (index < data.internal_menu.length) {
                                                            idProps = {
                                                                id: this.sectionId(data.internal_menu[index])
                                                            };
                                                        }
                                                    }

                                                    if (index === data.components.length - 1) {
                                                        idProps = {
                                                            id: LAST_COMPONENT_ID
                                                        };
                                                    }

                                                    if (!c.data) {
                                                        dataProps = {data: {empty: true}};
                                                    }

                                                    if (c.dataObj) {
                                                        dataProps = {data: c.dataObj};
                                                    }

                                                    return (
                                                        <ErrorBoundary key={"boundary-" + index}>
                                                            <CTemplateComponent {...idProps} key={"component-" + index}
                                                                                query={this.props.query}
                                                                                path={this.props.path}
                                                                                navigate={this.props.navigate}
                                                                                language={this.props.language}
                                                                                url={c.data}
                                                                                data-aos="fade-up" {...dataProps}
                                                                                layoutColumns={(!extraSmallLayout() && data.two_columns) ? 2 : 1}/>
                                                        </ErrorBoundary>
                                                    );

                                                } catch (e) {
                                                    console.warn("WARNING: Uncaught exception in component " + c.type, e);
                                                    return null;

                                                }
                                            })
                                        }
                                        {
                                            this.afterRightComponentsList(data)
                                        }
                                    </div>
                                    {
                                        this.props.path==="/" && <div className='bottom-background'/>
                                    }
                                </Col>
                            </Row>
                            <TemplatePageFooter
                                linksKeys={[...HOME_PAGE_FOOTER_LINKS, ...SUBPAGE_FOOTER_ADDITIONAL_LINKS]}/>
                        </>
                    }
                </Container>
            </div>
        );
    }

}

export default TemplatePage;
